import type { PaletteMode } from '@mui/material';
import { createTheme, type Theme } from '@mui/material/styles';
import { createTideTheme } from '@tide/dashboard-shell';
import { merge } from 'lodash';

import config, { ThemeMode, type ThemeDirection } from 'config';
import type { FontFamily, PresetColor } from 'types/config';
import componentsOverride from 'themes/overrides';
import Palette from 'themes/palette';
import CustomShadows from 'themes/shadows';
import Typography from 'themes/typography';

type ComponentOverrides = NonNullable<Theme['components']>;
type StyleOverrideArgs = { theme?: Theme; [key: string]: unknown };
type ResolvedStyle = Record<string, unknown>;
type StyleOverrideFn = (args: StyleOverrideArgs & { theme: Theme }) => ResolvedStyle;

type PortalDashboardThemeOptions = {
  fontFamily?: FontFamily;
  mode?: PaletteMode;
  presetColor?: PresetColor;
  themeDirection?: ThemeDirection;
};

export function createPortalDashboardTheme({
  fontFamily = config.fontFamily,
  mode = config.mode,
  presetColor = config.presetColor,
  themeDirection = config.themeDirection
}: PortalDashboardThemeOptions = {}): Theme {
  const portalMode = mode === ThemeMode.DARK ? ThemeMode.DARK : ThemeMode.LIGHT;
  const portalPalette = Palette(portalMode, presetColor);
  const theme = createTheme(
    createTideTheme({
      colorMode: portalMode,
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      shape: {
        borderRadius: 0
      }
    }),
    {
      direction: themeDirection,
      palette: portalPalette.palette,
      typography: Typography(fontFamily)
    }
  );

  theme.customShadows = CustomShadows(theme);
  theme.components = merge({}, theme.components, createPortalComponentOverrides(theme));

  return theme;
}

function createPortalComponentOverrides(theme: Theme): ComponentOverrides {
  const portalComponents = componentsOverride(theme) as ComponentOverrides;
  preserveShellOutlinedInputRoot(theme, portalComponents);

  return portalComponents;
}

function preserveShellOutlinedInputRoot(theme: Theme, portalComponents: ComponentOverrides) {
  const shellRoot = theme.components?.MuiOutlinedInput?.styleOverrides?.root;
  const portalRoot = portalComponents.MuiOutlinedInput?.styleOverrides?.root;
  const portalStyleOverrides = portalComponents.MuiOutlinedInput?.styleOverrides as Record<string, unknown> | undefined;

  if (!shellRoot || !portalRoot || !portalStyleOverrides) {
    return;
  }

  portalStyleOverrides.root = (args: StyleOverrideArgs) =>
    merge({}, resolveStyleOverride(shellRoot, args, theme), resolveStyleOverride(portalRoot, args, theme));
}

function resolveStyleOverride(value: unknown, args: StyleOverrideArgs, fallbackTheme: Theme): ResolvedStyle {
  if (typeof value === 'function') {
    return (value as StyleOverrideFn)({ ...args, theme: args.theme ?? fallbackTheme });
  }

  return value && typeof value === 'object' ? (value as ResolvedStyle) : {};
}
